import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Typography, Space, Button, Tooltip, Alert, Input } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { adminPath } from '../../../shared/config/paths';
import systemService from '../../services/systemService';

const { Title, Text } = Typography;

// system_settings 表：2026-10-07 全量核实 —— 没有任何后端/前端/节点代码读取这 49 项
// （控制器只做增删改查；VMsSyncMonitorService 的 config('system_settings.*') 指向不存在的配置文件，实际走 env）。
// 因此只读存档：保留数据，不再提供编辑/新增/删除，避免误以为改了会生效。
// 「真正生效的位置」：同一概念在别处有活的实现时给出跳转。
const LIVE_ELSEWHERE = {
  currency: { to: '/billing/currencies', text: '货币管理' },
  default_currency: { to: '/billing/currencies', text: '货币管理' },
  currency_symbol: { to: '/billing/currencies', text: '货币管理' },
  max_login_attempts: { to: '/system/rate-limit', text: '速率限制' },
  lockout_duration: { to: '/system/rate-limit', text: '速率限制' },
  default_cache_ttl: { to: '/cdn/domains', text: 'CDN 域名 › 缓存' },
  force_https: { to: '/cdn/domains', text: 'CDN 域名 › SSL' },
  enable_http2: { to: '/cdn/domains', text: 'CDN 域名 › 网络' },
  enable_http3: { to: '/system/cdn', text: 'CDN 全局设置' },
  allow_websocket: { to: '/cdn/domains', text: 'CDN 域名 › 网络' },
  default_registrar: { to: '/domain-registration/registrars', text: '域名注册商' },
};

const fmt = (row) => {
  if (row.key === 'smtp_password' || row.key === 'jwt_secret') return row.value ? '••••••' : '';
  if (row.type === 'boolean') return row.value === 'true' || row.value === '1' ? '开' : '关';
  return row.value;
};

const SystemSettings = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await systemService.getSettings();
      if (res.success) {
        const list = [];
        Object.entries(res.data || {}).forEach(([category, items]) => {
          (items || []).forEach((it) => list.push({ ...it, category }));
        });
        setRows(list);
      }
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const t = q.trim().toLowerCase();
  const data = t ? rows.filter((r) => [r.key, r.description, r.category].some((v) => String(v || '').toLowerCase().includes(t))) : rows;

  const columns = [
    { title: '分类', dataIndex: 'category', width: 90, render: (c) => <Tag>{c}</Tag> },
    {
      title: '名称',
      dataIndex: 'description',
      render: (d, r) => <Tooltip title={r.key}><span>{d || r.key}</span></Tooltip>,
    },
    { title: '存档值', key: 'value', ellipsis: true, render: (_, r) => <Text type="secondary">{fmt(r)}</Text> },
    {
      title: '实际生效位置',
      key: 'live',
      width: 180,
      render: (_, r) => (LIVE_ELSEWHERE[r.key]
        ? <Link to={adminPath(LIVE_ELSEWHERE[r.key].to)}>在「{LIVE_ELSEWHERE[r.key].text}」修改</Link>
        : <Text type="secondary">—</Text>),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>旧版设置项（未生效）</Title>
        <Space>
          <Input.Search placeholder="搜索" allowClear onChange={(e) => setQ(e.target.value)} style={{ width: 220 }} />
          <Button icon={<ReloadOutlined />} onClick={fetchSettings} loading={loading}>刷新</Button>
        </Space>
      </div>
      <Alert type="warning" showIcon style={{ marginBottom: 16 }} message="这些设置没有程序读取，只读存档。" />
      <Card size="small">
        <Table rowKey="key" size="small" loading={loading} columns={columns} dataSource={data} pagination={false} />
      </Card>
    </div>
  );
};

export default SystemSettings;
